/*
  Parallax Cyberpunk City – TypeScript-sichere Modul-Version
  ---------------------------------------------------------
  ✅ Fixes gegenüber vorher:
   - Kein "sCtx possibly undefined": 2D-Kontexte werden sauber geprüft.
   - Alle Klassen (Vehicle, NeonSign, RainDrop, GroundVehicle) haben deklarierte Properties.
   - Strikte Typen für Layer/Building/etc.
   - Gemeinsames API: tick(dt), start/stop, resize, setSpeed, setWords, destroy.

  Nutzung (ES-Module in Angular/Plain TS):
    import { createParallaxCity, ParallaxAPI } from './parallax-city.ts';
    const api: ParallaxAPI = createParallaxCity(document.querySelector('#bgCanvas')!, {
      speed: 3,
      words: ['CYPHER','GANG','$CYPHR','XPR']
    });
    // Im gemeinsamen Game-Loop: api.tick(dt)
*/

export type ParallaxOptions = {
  speed: number;
  vehicleCount: number;
  rainCount: number;
  enableLightning: boolean;
  showCRT: boolean;
  words: string[];
};

export type ParallaxAPI = {
  tick: (dt?: number) => void;
  start: () => void;
  stop: () => void;
  resize: () => void;
  setSpeed: (v: number) => void;
  setWords: (w: string[]) => void;
  destroy: () => void;
};

function get2D(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const c = canvas.getContext('2d');
  if (!c) throw new Error('Canvas 2D Context wird nicht unterstützt.');
  // In einigen Browsern fehlt der TS-Typ für imageSmoothingEnabled → optional setzen
  (c as CanvasRenderingContext2D & { imageSmoothingEnabled?: boolean }).imageSmoothingEnabled =
    false;
  return c as CanvasRenderingContext2D;
}

// ---------- Typen ----------
interface Layer<T = unknown> {
  offset: number;
  speed: number;
  elements: T[];
  signs: T[];
}
interface Star {
  x: number;
  y: number;
  size: number;
  twinklePhase: number;
  twinkleSpeed: number;
}
interface WindowLight {
  x: number;
  y: number;
  lit: boolean;
  color: string;
  flickerPhase: number;
  flickerSpeed: number;
}
interface Antenna {
  x: number;
  height: number;
  blinkPhase: number;
}
interface Building {
  x: number;
  width: number;
  height: number;
  windows: WindowLight[];
  antennas: Antenna[];
  color: string;
  hasBillboard: boolean;
}

export function createParallaxCity(
  canvas: HTMLCanvasElement,
  opts: Partial<ParallaxOptions> = {}
): ParallaxAPI {
  const ctx = get2D(canvas);

  const options: ParallaxOptions = {
    speed: 3,
    vehicleCount: 5,
    rainCount: 100,
    enableLightning: true,
    showCRT: true,
    words: ['CYBER', 'NEON', 'TECH', 'CITY'],
    ...opts,
  };

  // ---------- State ----------
  let scrollSpeed = options.speed;
  let time = 0;
  let raf = 0,
    running = false,
    lastTs = 0;

  // ---------- Layers ----------
  const layers: Layer[] = [
    { offset: 0, speed: 0.1, elements: [], signs: [] }, // Stars
    { offset: 0, speed: 0.3, elements: [], signs: [] },
    { offset: 0, speed: 0.5, elements: [], signs: [] },
    { offset: 0, speed: 0.8, elements: [], signs: [] },
    { offset: 0, speed: 1.0, elements: [], signs: [] },
  ];

  // ---------- Klassen ----------
  class Vehicle {
    x = 0;
    y = 0;
    speed = 0;
    color = '#ffffff';
    trail: { x: number; y: number; opacity: number }[] = [];
    blinkPhase = 0;
    constructor(
      private readonly canvas: HTMLCanvasElement,
      private readonly ctx: CanvasRenderingContext2D
    ) {
      this.reset();
    }
    reset(): void {
      this.x = Math.random() > 0.5 ? -50 : this.canvas.width + 50;
      this.y = Math.random() * this.canvas.height * 0.4 + 50;
      this.speed = (Math.random() * 3 + 2) * (this.x > this.canvas.width ? -1 : 1);
      this.color = ['#ff00ff', '#00ffff', '#ffff00'][Math.floor(Math.random() * 3)];
      this.trail = [];
      this.blinkPhase = Math.random() * Math.PI * 2;
    }
    update(): void {
      this.x += this.speed;
      this.blinkPhase += 0.3;
      this.trail.push({ x: this.x, y: this.y, opacity: 1 });
      if (this.trail.length > 20) this.trail.shift();
      this.trail.forEach((t) => (t.opacity *= 0.9));
      if ((this.speed > 0 && this.x > this.canvas.width + 50) || (this.speed < 0 && this.x < -50))
        this.reset();
    }
    draw(): void {
      this.ctx.strokeStyle = this.color;
      this.ctx.globalAlpha = 0.3;
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.trail.forEach((t, i) => {
        if (i === 0) this.ctx.moveTo(t.x, t.y);
        else this.ctx.lineTo(t.x, t.y);
      });
      this.ctx.stroke();
      this.ctx.globalAlpha = 0.8 + Math.sin(this.blinkPhase) * 0.2;
      this.ctx.fillStyle = this.color;
      this.ctx.fillRect(this.x - 4, this.y - 2, 8, 4);
      if (Math.sin(this.blinkPhase) > 0) {
        this.ctx.shadowColor = this.color;
        this.ctx.shadowBlur = 15;
        this.ctx.fillRect(this.x - 2, this.y - 1, 4, 2);
        this.ctx.shadowBlur = 0;
      }
      this.ctx.globalAlpha = 1;
    }
  }

  class NeonSign {
    phase = Math.random() * Math.PI * 2;
    color = ['#ff00ff', '#00ffff', '#ff0080'][Math.floor(Math.random() * 3)];
    flickerSpeed = Math.random() * 0.2 + 0.1;
    constructor(public x: number, public y: number, public text: string | undefined) {}
    draw(offset: number): void {
      const drawX = this.x - offset;
      this.phase += this.flickerSpeed;
      ctx.save();
      ctx.font = 'bold 16px monospace';
      ctx.fillStyle = this.color;
      ctx.strokeStyle = this.color;
      ctx.lineWidth = 2;
      const brightness = Math.sin(this.phase) * 0.3 + 0.7;
      ctx.globalAlpha = brightness;
      ctx.shadowColor = this.color;
      ctx.shadowBlur = 20 * brightness;
      ctx.fillText(this.text || pickWord(), drawX, this.y);
      if (Math.random() > 0.95) {
        ctx.shadowBlur = 40;
        ctx.fillText(this.text || pickWord(), drawX, this.y);
      }
      ctx.restore();
    }
  }

  class RainDrop {
    x = 0;
    y = -10;
    speed = 0;
    length = 0;
    opacity = 0.2;
    constructor(
      private readonly canvas: HTMLCanvasElement,
      private readonly ctx: CanvasRenderingContext2D
    ) {
      this.reset();
    }
    reset(): void {
      this.x = Math.random() * this.canvas.width;
      this.y = -10;
      this.speed = Math.random() * 15 + 10;
      this.length = Math.random() * 20 + 10;
      this.opacity = Math.random() * 0.3 + 0.1;
    }
    update(): void {
      this.y += this.speed;
      if (this.y > this.canvas.height) this.reset();
    }
    draw(): void {
      this.ctx.strokeStyle = `rgba(100,200,255,${this.opacity})`;
      this.ctx.lineWidth = 1;
      this.ctx.beginPath();
      this.ctx.moveTo(this.x, this.y);
      this.ctx.lineTo(this.x, this.y - this.length);
      this.ctx.stroke();
    }
  }

  // ---------- Collections ----------
  const vehicles: Vehicle[] = Array.from(
    { length: Math.max(0, options.vehicleCount | 0) },
    () => new Vehicle(canvas, ctx)
  );
  const rain: RainDrop[] = Array.from(
    { length: Math.max(0, options.rainCount | 0) },
    () => new RainDrop(canvas, ctx)
  );

  // ---------- Lightning ----------
  const lightning: {
    active: boolean;
    timer: number;
    x: number;
    branches: { x: number; y: number }[];
  } = { active: false, timer: 0, x: 0, branches: [] };

  // ---------- Skyline Texture (optional) ----------
  let skylineCanvas: HTMLCanvasElement | null = null;
  let skylinePattern: CanvasPattern | null = null;

  function pickWord(): string {
    return options.words && options.words.length
      ? options.words[Math.floor(Math.random() * options.words.length)]
      : 'NEON';
  }

  function createSkylineTexture(): void {
    skylineCanvas = document.createElement('canvas');
    skylineCanvas.width = 600;
    skylineCanvas.height = 150;
    const sCtx = skylineCanvas.getContext('2d');
    if (!sCtx) return; // ← Fix: kein undefined mehr
    (sCtx as CanvasRenderingContext2D & { imageSmoothingEnabled?: boolean }).imageSmoothingEnabled =
      false;

    sCtx.fillStyle = '#050510';
    sCtx.fillRect(0, 0, skylineCanvas.width, skylineCanvas.height);
    const blockSize = 12;
    const blocks: { x: number; y: number; w: number; h: number; lit: boolean; color: string }[] =
      [];
    for (let x = 0; x < skylineCanvas.width; x += blockSize * 2) {
      for (let y = 0; y < skylineCanvas.height; y += blockSize * 2) {
        if (Math.random() > 0.3)
          blocks.push({
            x: x + Math.random() * blockSize,
            y: y + Math.random() * blockSize,
            w: blockSize + Math.random() * blockSize,
            h: blockSize + Math.random() * blockSize,
            lit: Math.random() > 0.3,
            color: Math.random() > 0.5 ? '#0a0a15' : '#151520',
          });
      }
    }
    for (const b of blocks) {
      sCtx.fillStyle = b.color;
      sCtx.fillRect(b.x, b.y, b.w, b.h);
      sCtx.strokeStyle = '#1a1a2e';
      sCtx.lineWidth = 1;
      sCtx.strokeRect(b.x, b.y, b.w, b.h);
      if (b.lit) {
        const num = Math.floor(Math.random() * 4 + 1);
        for (let i = 0; i < num; i++) {
          const lx = b.x + 2 + Math.random() * (b.w - 4);
          const ly = b.y + 2 + Math.random() * (b.h - 4);
          sCtx.fillStyle = ['#ffffff', '#fff0ff', '#ffff0f', '#00fff0'][
            Math.floor(Math.random() * 4)
          ];
          sCtx.globalAlpha = 0.6 + Math.random() * 0.4;
          sCtx.fillRect(lx, ly, 4, 4);
        }
        sCtx.globalAlpha = 1;
      }
    }
    sCtx.strokeStyle = '#222';
    sCtx.lineWidth = 2;
    for (let x = 0; x < skylineCanvas.width; x += blockSize * 4) {
      sCtx.beginPath();
      sCtx.moveTo(x, 0);
      sCtx.lineTo(x, skylineCanvas.height);
      sCtx.stroke();
    }
    for (let y = 0; y < skylineCanvas.height; y += blockSize * 4) {
      sCtx.beginPath();
      sCtx.moveTo(0, y);
      sCtx.lineTo(skylineCanvas.width, y);
      sCtx.stroke();
    }

    skylinePattern = ctx.createPattern(skylineCanvas, 'repeat');
  }

  // ---------- Stars ----------
  function initStars(): void {
    layers[0].elements = [];
    for (let i = 0; i < 100; i++) {
      layers[0].elements.push({
        x: Math.random() * canvas.width * 3,
        y: Math.random() * canvas.height * 0.5,
        size: Math.random() > 0.95 ? 2 : 1,
        twinklePhase: Math.random() * Math.PI * 2,
        twinkleSpeed: Math.random() * 0.1 + 0.05,
      } as Star);
    }
  }

  function drawStars(layer: Layer<Star>): void {
    for (const star of layer.elements) {
      const x = (star.x - layer.offset) % (canvas.width * 2);
      const ax = x < 0 ? x + canvas.width * 2 : x;
      if (ax < canvas.width + 10) {
        star.twinklePhase += star.twinkleSpeed;
        const brightness = Math.sin(star.twinklePhase) * 0.5 + 0.5;
        ctx.fillStyle = `rgba(255,255,255,${brightness})`;
        ctx.fillRect(ax, star.y, star.size * 2, star.size * 2);
        if (star.size > 1 && brightness > 0.7) {
          ctx.shadowColor = '#ffffff';
          ctx.shadowBlur = 10;
          ctx.fillRect(ax, star.y, star.size, star.size);
          ctx.shadowBlur = 0;
        }
      }
    }
  }

  // ---------- Buildings ----------
  function generateBuilding(x: number, layerIndex: number): Building {
    const width = Math.floor(Math.random() * 80 + 60);
    const height = Math.floor(Math.random() * 250 + 150) * (0.4 + layerIndex * 0.2);
    const windows: WindowLight[] = [];
    const antennas: Antenna[] = [];
    const windowSize = 6;
    const spacing = 12;
    for (let wx = spacing; wx < width - spacing; wx += spacing + windowSize) {
      for (let wy = spacing; wy < height - spacing * 2; wy += spacing + windowSize) {
        windows.push({
          x: wx,
          y: wy,
          lit: Math.random() > 0.2,
          color: ['#00ffff', '#ff00ff', '#ffff00', '#00ff00'][Math.floor(Math.random() * 4)],
          flickerPhase: Math.random() * Math.PI * 2,
          flickerSpeed: Math.random() * 0.1 + 0.05,
        });
      }
    }
    if (Math.random() > 0.5) {
      for (let i = 0; i < Math.random() * 3 + 1; i++) {
        antennas.push({
          x: Math.random() * width,
          height: Math.random() * 30 + 10,
          blinkPhase: Math.random() * Math.PI * 2,
        });
      }
    }
    return {
      x,
      width,
      height,
      windows,
      antennas,
      color: `hsl(${200 + Math.random() * 60}, 30%, ${10 + Math.random() * 10}%)`,
      hasBillboard: Math.random() > 0.7 && layerIndex >= 2,
    };
  }

  function initBuildings(): void {
    for (let l = 1; l < layers.length; l++) {
      const layer = layers[l];
      layer.elements = [];
      layer.signs = [];
      let x = 0;
      while (x < canvas.width * 2) {
        const b = generateBuilding(x, l);
        layer.elements.push(b);
        if (b.hasBillboard)
          layer.signs.push(new NeonSign(x + b.width / 2, canvas.height - b.height - 20, undefined));
        x += b.width + Math.random() * 30 + 20;
      }
    }
  }

  function drawBuilding(building: Building, layer: Layer, layerIndex: number): void {
    const x = (building.x - layer.offset) % (canvas.width * 2);
    const ax = x < 0 ? x + canvas.width * 2 : x;
    if (ax > -building.width && ax < canvas.width + building.width) {
      const baseY = canvas.height - building.height;
      const grad = ctx.createLinearGradient(ax, baseY, ax, canvas.height);
      grad.addColorStop(0, building.color);
      grad.addColorStop(1, 'black');
      ctx.fillStyle = grad;
      ctx.fillRect(ax, baseY, building.width, building.height);
      for (const w of building.windows) {
        if (w.lit) {
          w.flickerPhase += w.flickerSpeed;
          const f = Math.sin(w.flickerPhase) * 0.3 + 0.7;
          if (Math.random() > 0.999) w.lit = false;
          if (Math.random() > 0.998) w.lit = true;
          ctx.fillStyle = w.color;
          ctx.globalAlpha = f;
          ctx.fillRect(ax + w.x, baseY + w.y, layerIndex * 2, layerIndex * 2);
          if (f > 0.8) {
            ctx.shadowColor = w.color;
            ctx.shadowBlur = 5;
            ctx.fillRect(ax + w.x, baseY + w.y, 6, 6);
            ctx.shadowBlur = 0;
          }
        }
      }
      ctx.globalAlpha = 1;
      for (const a of building.antennas) {
        ctx.strokeStyle = '#444';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(ax + a.x, baseY);
        ctx.lineTo(ax + a.x, baseY - a.height);
        ctx.stroke();
        a.blinkPhase += 0.05;
        if (Math.sin(a.blinkPhase) > 0) {
          ctx.fillStyle = '#ff0000';
          ctx.shadowColor = '#ff0000';
          ctx.shadowBlur = 10;
          ctx.beginPath();
          ctx.arc(ax + a.x, baseY - a.height, 3, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        }
      }
    }
  }

  // function drawGround(layer: Layer): void { groundVehicles.forEach(g => { g.update(layer.offset); g.draw(layer.offset); }); }

  // ---------- Lightning ----------
  function triggerLightning(): void {
    if (!options.enableLightning) return;
    if (Math.random() > 0.995 && !lightning.active) {
      lightning.active = true;
      lightning.timer = 10;
      lightning.x = Math.random() * canvas.width;
      lightning.branches = [];
      let y = 0,
        x = lightning.x;
      while (y < canvas.height * 0.7) {
        y += Math.random() * 30 + 10;
        x += (Math.random() - 0.5) * 50;
        lightning.branches.push({ x, y });
      }
    }
  }
  function drawLightning(): void {
    if (!lightning.active) return;
    lightning.timer--;
    if (lightning.timer <= 0) {
      lightning.active = false;
      return;
    }
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = lightning.timer > 5 ? 3 : 1;
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 30;
    ctx.globalAlpha = lightning.timer / 10;
    ctx.beginPath();
    ctx.moveTo(lightning.x, 0);
    lightning.branches.forEach((b) => ctx.lineTo(b.x, b.y));
    ctx.stroke();
    if (lightning.timer > 7) {
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;
  }

  // ---------- Effekte ----------
  function scanlinesAndVignette(): void {
    if (!options.showCRT) return;
    ctx.fillStyle = 'rgba(0,0,0,0.1)';
    for (let y = 0; y < canvas.height; y += 3) ctx.fillRect(0, y, canvas.width, 1);
    const vignette = ctx.createRadialGradient(
      canvas.width / 2,
      canvas.height / 2,
      0,
      canvas.width / 2,
      canvas.height / 2,
      canvas.width * 0.7
    );
    vignette.addColorStop(0, 'transparent');
    vignette.addColorStop(1, 'rgba(0,0,0,0.4)');
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  // ---------- Resize ----------
  function resize(): void {
    const w = canvas.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || window.innerHeight;
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    (ctx as CanvasRenderingContext2D & { imageSmoothingEnabled?: boolean }).imageSmoothingEnabled =
      false;
    initStars();
    initBuildings();
  }

  // ---------- Render-Schritt ----------
  function tick(dt = 1): void {
    time += 16 * dt;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawSky();

    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i];
      layer.offset += scrollSpeed * layer.speed * dt;
      if (i === 0) drawStars(layer as Layer<Star>);
      else {
        ctx.fillStyle = `rgba(100, 0, 200, ${i * 0.02})`;
        ctx.fillRect(0, canvas.height - 100 * i, canvas.width, 100 * i);
        for (const b of layer.elements as Building[]) drawBuilding(b, layer, i);
        for (const s of layer.signs) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          s.draw(layer.offset);
        }
        // Nachladen/Aufräumen
        if (layer.elements.length > 0) {
          const last = layer.elements[layer.elements.length - 1] as Building;
          const lastRight = last.x + last.width - layer.offset;
          if (lastRight < canvas.width * 1.5) {
            const newX = last.x + last.width + Math.random() * 30 + 20;
            const nb = generateBuilding(newX, i);
            layer.elements.push(nb);
            if (nb.hasBillboard)
              layer.signs.push(
                new NeonSign(newX + nb.width / 2, canvas.height - nb.height - 20, undefined)
              );
          }
          while (
            layer.elements.length > 0 &&
            (layer.elements[0] as Building).x +
              (layer.elements[0] as Building).width -
              layer.offset <
              -canvas.width
          ) {
            layer.elements.shift();
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            if (layer.signs.length > 0 && layer.signs[0].x - layer.offset < -canvas.width)
              layer.signs.shift();
          }
        }
      }
    }

    vehicles.forEach((v) => {
      v.update();
      v.draw();
    });
    rain.forEach((r) => {
      r.update();
      r.draw();
    });
    // drawGround(layers[1]);
    triggerLightning();
    drawLightning();
    scanlinesAndVignette();
  }

  // ---------- Sky ----------
  function drawSky(): void {
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    const hue = 220 + Math.sin(time * 0.0001) * 20;
    gradient.addColorStop(0, `hsl(${hue}, 50%, 5%)`);
    gradient.addColorStop(0.3, `hsl(${hue + 20}, 40%, 10%)`);
    gradient.addColorStop(0.7, `hsl(${hue + 40}, 35%, 15%)`);
    gradient.addColorStop(1, `hsl(280, 40%, 20%)`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Subtle skyline pattern overlay (uses generated pattern)
    if (skylinePattern) {
      ctx.save();
      ctx.globalAlpha = 0.06;
      ctx.fillStyle = skylinePattern;
      ctx.fillRect(0, canvas.height - 150, canvas.width, 150);
      ctx.restore();
    }

    ctx.fillStyle = 'rgba(255, 0, 255, 0.02)';
    for (let i = 0; i < 3; i++) {
      ctx.save();
      ctx.globalAlpha = 0.1;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.scale(2 + Math.sin(time * 0.0002 + i) * 0.5, 1 + Math.cos(time * 0.0003 + i) * 0.3);
      ctx.beginPath();
      ctx.arc(
        Math.sin(time * 0.0001 * (i + 1)) * 200,
        Math.cos(time * 0.0001 * (i + 1)) * 100,
        200,
        0,
        Math.PI * 2
      );
      ctx.fill();
      ctx.restore();
    }
  }

  // ---------- Public API ----------
  function setSpeed(v: number): void {
    scrollSpeed = Math.max(0, Number(v) || 0);
  }
  function setWords(words: string[]): void {
    if (Array.isArray(words) && words.length) options.words = words;
  }
  function start(): void {
    if (running) return;
    running = true;
    lastTs = performance.now();
    const loop = (ts: number) => {
      const dt = Math.min(2, ((ts - lastTs) / 1000) * 60 || 1);
      lastTs = ts;
      tick(dt);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
  }
  function stop(): void {
    running = false;
    if (raf) cancelAnimationFrame(raf);
  }
  function destroy(): void {
    stop(); /* derzeit nichts aufzuräumen */
  }

  // ---------- Init ----------
  resize();
  createSkylineTexture();
  initStars();
  initBuildings();

  return { tick, start, stop, resize, setSpeed, setWords, destroy };
}
